import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useDeleteSchedule, useSchedules } from "../api/use-schedules";
import { ScheduleListRow } from "./schedule-list-row";
import { SchedulesEmptyState } from "./schedules-empty-state";

export function SchedulesPage() {
  const { data: schedules, isLoading, isError, error } = useSchedules();
  const deleteSchedule = useDeleteSchedule();

  const handleDelete = async (id: string) => {
    try {
      await deleteSchedule.mutateAsync(id);
      toast.success("Schedule deleted");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to delete schedule";
      toast.error(message);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Schedules</h1>
          <p className="text-sm text-text-muted mt-1">
            One schedule per school year — every subject area lives in a single
            Outlook calendar.
          </p>
        </div>
        <Link
          to="/schedules/new"
          data-testid="header-new-schedule"
          className="inline-flex items-center gap-1.5 bg-brand hover:bg-brand-hover text-white text-sm font-semibold rounded-md px-4 py-2"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          New Schedule
        </Link>
      </div>

      {isLoading && (
        <div
          data-testid="schedules-loading"
          className="py-16 flex justify-center"
        >
          <LoadingSpinner size="lg" />
        </div>
      )}

      {isError && (
        <div
          data-testid="schedules-error"
          className="bg-surface border border-red-500/30 rounded-lg p-6 text-center"
        >
          <p className="text-sm text-red-400">
            {error instanceof Error
              ? error.message
              : "Failed to load schedules."}
          </p>
        </div>
      )}

      {!isLoading && !isError && schedules && schedules.length === 0 && (
        <SchedulesEmptyState />
      )}

      {!isLoading && !isError && schedules && schedules.length > 0 && (
        <div
          data-testid="schedules-list"
          className="flex flex-col gap-3 max-w-3xl"
        >
          {schedules.map((schedule) => (
            <ScheduleListRow
              key={schedule.id}
              schedule={schedule}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
